/**
 * CrewLandingPage ✦ — EventDrop Crew /crew
 *
 * Standalone marketing landing for the Crew sub-product. Uses the
 * neon-purple/pink/blue palette from the product spec (deliberately
 * distinct from the editorial walnut/charcoal of the rest of
 * EventDrop). The brand sits inside a glassmorphism vertical
 * composition with large bold headings + minimal text.
 *
 * Sections per spec:
 *   1. Hero (headline + Join Crew / Post Opportunity CTAs + phone mock)
 *   2. How It Works (4 steps)
 *   3. EventDrop Team — "Find reliable crew in 30 seconds"
 *   4. EventDrop Crew — "Build your future one event at a time"
 *   5. Live Opportunities (auto-scrolling card preview)
 *   6. Badges & Progression
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  SealCheck, Sparkle, Lightning, HandHeart, Users, Trophy,
  Camera, MusicNote, Confetti, Buildings,
} from '@phosphor-icons/react';
import CrewSpotlightRow from '../../components/crew/CrewSpotlightRow';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Crew palette (per spec; namespaced to /crew/* surfaces only) ──
const BG = '#07071A';
const PANEL = 'rgba(255,255,255,0.04)';
const PANEL_BORDER = 'rgba(255,255,255,0.08)';
const PURPLE = '#6C3BFF';
const PINK = '#FF3CAC';
const BLUE = '#33C3FF';
const TEXT = '#FFFFFF';
const TEXT_MUTED = '#B8B8D1';

const FONT_STACK = `'Inter', 'Poppins', -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif`;

const HeroCta = ({ label, primary, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    style={{
      padding: '16px 28px',
      borderRadius: 999,
      border: 'none',
      cursor: 'pointer',
      fontFamily: FONT_STACK,
      fontWeight: 800,
      fontSize: 15,
      letterSpacing: '-0.01em',
      color: TEXT,
      background: primary
        ? `linear-gradient(135deg, ${PURPLE} 0%, ${PINK} 100%)`
        : `linear-gradient(135deg, rgba(108,59,255,0.18) 0%, rgba(51,195,255,0.18) 100%)`,
      boxShadow: primary
        ? `0 8px 24px rgba(108,59,255,0.32), 0 0 0 1px rgba(255,255,255,0.06) inset`
        : `0 4px 12px rgba(0,0,0,0.3), 0 0 0 1px ${PANEL_BORDER} inset`,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    {label}
  </button>
);

const SectionEyebrow = ({ children }) => (
  <p style={{
    margin: 0, fontFamily: FONT_STACK, fontSize: 11, fontWeight: 700,
    letterSpacing: '0.32em', textTransform: 'uppercase',
    background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  }}>
    {children}
  </p>
);

const StepCard = ({ num, title, icon: Icon }) => (
  <div style={{
    flex: '1 1 200px', minWidth: 200,
    padding: '24px 22px',
    background: PANEL, border: `1px solid ${PANEL_BORDER}`,
    borderRadius: 20, backdropFilter: 'blur(12px)',
    position: 'relative', overflow: 'hidden',
  }}>
    <div style={{
      position: 'absolute', top: -20, right: -20, width: 80, height: 80,
      borderRadius: '50%', background: `radial-gradient(circle, ${PURPLE}55, transparent 70%)`,
      pointerEvents: 'none',
    }} />
    <div style={{
      width: 38, height: 38, borderRadius: 10,
      background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: FONT_STACK, fontWeight: 900, fontSize: 17, color: TEXT,
      marginBottom: 16,
    }}>{num}</div>
    <Icon size={22} weight="fill" color={BLUE} style={{ marginBottom: 12 }} />
    <p style={{
      margin: 0, fontFamily: FONT_STACK, fontWeight: 700, fontSize: 16,
      color: TEXT, lineHeight: 1.35,
    }}>{title}</p>
  </div>
);

const FeatureRow = ({ label }) => (
  <li style={{
    padding: '10px 0', borderTop: `1px solid ${PANEL_BORDER}`,
    fontFamily: FONT_STACK, fontSize: 14.5, color: TEXT_MUTED,
    display: 'flex', alignItems: 'center', gap: 10,
  }}>
    <Sparkle size={12} weight="fill" color={PINK} />
    <span>{label}</span>
  </li>
);

const OpportunityPreviewCard = ({ title, supplier, pay, location, when, tags, accent }) => (
  <div style={{
    minWidth: 270, maxWidth: 270,
    background: `linear-gradient(180deg, ${PANEL} 0%, rgba(255,255,255,0.02) 100%)`,
    border: `1px solid ${PANEL_BORDER}`, borderRadius: 18,
    padding: 18, backdropFilter: 'blur(10px)',
    position: 'relative', overflow: 'hidden',
  }}>
    <div style={{
      position: 'absolute', top: 0, right: 0, width: 120, height: 120,
      borderRadius: '50%', background: `radial-gradient(circle, ${accent}55, transparent 60%)`,
      pointerEvents: 'none',
    }} />
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, position: 'relative' }}>
      {tags.map((t) => (
        <span key={t} style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
          padding: '3px 8px', borderRadius: 999,
          background: `${PINK}22`, color: PINK,
          border: `1px solid ${PINK}55`,
        }}>{t}</span>
      ))}
    </div>
    <p style={{ margin: 0, fontFamily: FONT_STACK, fontWeight: 800, fontSize: 18, color: TEXT, lineHeight: 1.3 }}>
      {title}
    </p>
    <p style={{ margin: '6px 0 14px', fontSize: 13, color: TEXT_MUTED, fontFamily: FONT_STACK }}>
      {supplier} · {location}
    </p>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <p style={{ margin: 0, fontFamily: FONT_STACK, fontWeight: 800, fontSize: 16, color: BLUE }}>{pay}</p>
      <p style={{ margin: 0, fontFamily: FONT_STACK, fontSize: 12, color: TEXT_MUTED }}>{when}</p>
    </div>
  </div>
);

const BadgeChip = ({ label, color }) => (
  <div style={{
    padding: '14px 18px', borderRadius: 999,
    background: `linear-gradient(135deg, ${color}33, ${color}11)`,
    border: `1px solid ${color}55`,
    fontFamily: FONT_STACK, fontWeight: 800, fontSize: 13, color: TEXT,
    display: 'inline-flex', alignItems: 'center', gap: 8,
    boxShadow: `0 0 24px ${color}22`,
  }}>
    <Trophy size={13} weight="fill" color={color} />
    {label}
  </div>
);

const CrewLandingPage = () => {
  const navigate = useNavigate();
  const [livePreview, setLivePreview] = useState([]);

  useEffect(() => {
    // Pull a small sample of real live opportunities for the
    // auto-scrolling preview row. Falls back to a static demo set
    // when the backend has no data yet so the marketing surface
    // never reads empty.
    (async () => {
      try {
        const r = await axios.get(`${API}/crew/feed?limit=8`);
        const live = r.data?.opportunities || [];
        if (live.length > 0) setLivePreview(live);
      } catch { /* fall through to demo */ }
    })();
  }, []);

  // Demo cards shown when no live data yet — gives the marketing
  // page real visual energy from day one without requiring seed
  // data on every fresh environment.
  const DEMO = [
    { title: 'Photobooth Assistant', supplier: 'Quiet Lens Studio', pay: '£12/hr', location: 'London', when: 'Sat 14 Jun', tags: ['Urgent', 'Beginner Friendly'], accent: PINK },
    { title: 'Wedding Setup Crew', supplier: 'Riverside Hall', pay: '£60 flat', location: 'Bristol', when: 'Fri 20 Jun', tags: ['Today', 'Bring a Friend'], accent: PURPLE },
    { title: 'Festival Crew', supplier: 'Sound Field Co.', pay: '£14/hr', location: 'Brighton', when: 'Sat 28 Jun', tags: ['Beginner Friendly'], accent: BLUE },
    { title: 'DJ Assistant', supplier: 'After Hours', pay: '£15/hr', location: 'Manchester', when: 'Sat 21 Jun', tags: ['Trial Shift'], accent: PINK },
    { title: 'Venue Helper', supplier: 'The Old Mill', pay: '£10/hr', location: 'Leeds', when: 'Sun 22 Jun', tags: ['Ongoing Role'], accent: BLUE },
  ];

  const previewCards = livePreview.length > 0
    ? livePreview.slice(0, 6).map((o) => ({
        title: o.title,
        supplier: o.supplier_name,
        pay: o.pay,
        location: o.location,
        when: o.event_date,
        tags: (o.tags || []).slice(0, 2).map((t) => t.replace(/_/g, ' ')),
        accent: (o.tags || []).includes('urgent') ? PINK : (o.tags || []).includes('beginner_friendly') ? BLUE : PURPLE,
      }))
    : DEMO;

  return (
    <div
      data-testid="crew-landing-page"
      style={{
        minHeight: '100vh', background: BG, color: TEXT,
        fontFamily: FONT_STACK, overflowX: 'hidden',
      }}
    >
      {/* Ambient radial backdrop */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `
          radial-gradient(circle at 15% 20%, ${PURPLE}26 0%, transparent 38%),
          radial-gradient(circle at 85% 70%, ${PINK}1F 0%, transparent 42%),
          radial-gradient(circle at 50% 100%, ${BLUE}1A 0%, transparent 40%)
        `,
      }} />

      <div style={{ position: 'relative', zIndex: 1, padding: '0 clamp(20px, 5vw, 64px)' }}>

        {/* ── NAV ── */}
        <nav style={{
          padding: '24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <a href="/" data-testid="crew-nav-logo" style={{
            fontFamily: FONT_STACK, fontWeight: 900, fontSize: 17, color: TEXT,
            textDecoration: 'none', letterSpacing: '-0.01em',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{
              width: 26, height: 26, borderRadius: 8,
              background: `linear-gradient(135deg, ${PURPLE}, ${PINK})`,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 900,
            }}>✦</span>
            EventDrop <span style={{ color: TEXT_MUTED, fontWeight: 500, marginLeft: 4 }}>Crew</span>
          </a>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <button
              onClick={() => navigate('/login')}
              data-testid="crew-nav-login"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontFamily: FONT_STACK, fontWeight: 600, fontSize: 14, color: TEXT_MUTED,
              }}
            >Sign in</button>
            <HeroCta
              label="Join Crew"
              primary
              onClick={() => navigate('/crew/feed')}
              testId="crew-nav-join"
            />
          </div>
        </nav>

        {/* ── HERO ── */}
        <section style={{
          padding: 'clamp(40px, 8vw, 96px) 0',
          display: 'grid', gap: 'clamp(32px, 6vw, 64px)',
          gridTemplateColumns: 'minmax(0, 1fr)',
          alignItems: 'center',
        }}>
          <div>
            <SectionEyebrow>✦ EventDrop Team · for aspiring young crew</SectionEyebrow>
            <h1 style={{
              margin: '20px 0 18px', fontFamily: FONT_STACK,
              fontWeight: 900, fontSize: 'clamp(38px, 7vw, 76px)',
              letterSpacing: '-0.025em', lineHeight: 1.02, color: TEXT,
            }}>
              Real events.<br/>
              Real experience.<br/>
              <span style={{
                background: `linear-gradient(135deg, ${PINK}, ${BLUE})`,
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>Real you.</span>
            </h1>
            <p style={{
              margin: '0 0 32px', maxWidth: 540, fontFamily: FONT_STACK, fontSize: 18,
              color: TEXT_MUTED, lineHeight: 1.55,
            }}>
              Helping aspiring young people build confidence and gain real event experience.
              Swipe opportunities. Tap to apply. Show up. Level up.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <HeroCta label="Join Crew" primary onClick={() => navigate('/crew/feed')} testId="hero-join-crew" />
              <HeroCta label="Post Opportunity" onClick={() => navigate('/dashboard/supplier/crew/new')} testId="hero-post-opportunity" />
            </div>

            {/* ── Swipe Tutorial Animation ──
                Tiny CSS-only loop teaching the gesture in 4 seconds.
                Photobooth card appears → finger drags right → heart
                pulse + "Interested ❤️" → snaps back. No JS, no
                Lottie. Performs perfectly on mobile. */}
            <div
              data-testid="swipe-tutorial"
              style={{
                marginTop: 44, position: 'relative',
                maxWidth: 280, height: 360, perspective: 900,
              }}
              aria-hidden="true"
            >
              <style>{`
                @keyframes cardSwipeLoop {
                  0%, 12%   { transform: translateX(0) rotate(0deg); opacity: 1; }
                  35%       { transform: translateX(85px) rotate(11deg); opacity: 1; }
                  55%       { transform: translateX(220px) rotate(18deg); opacity: 0; }
                  56%       { transform: translateX(-260px) rotate(-12deg); opacity: 0; }
                  70%, 100% { transform: translateX(0) rotate(0deg); opacity: 1; }
                }
                @keyframes fingerLoop {
                  0%, 12%   { transform: translate(0, 0) scale(1); opacity: 0; }
                  18%       { transform: translate(0, 0) scale(1); opacity: 1; }
                  35%       { transform: translate(90px, -8px) scale(0.92); opacity: 1; }
                  50%       { transform: translate(160px, -20px) scale(0.9); opacity: 0.4; }
                  55%, 100% { transform: translate(0, 0) scale(1); opacity: 0; }
                }
                @keyframes likeBadgePulse {
                  0%, 30%   { transform: scale(0.6) translateY(8px); opacity: 0; }
                  42%, 52%  { transform: scale(1) translateY(0); opacity: 1; }
                  62%, 100% { transform: scale(1) translateY(-4px); opacity: 0; }
                }
                @keyframes heartBeat {
                  0%, 100% { transform: scale(1); }
                  50%      { transform: scale(1.18); }
                }
                @keyframes likeGlow {
                  0%, 30%   { opacity: 0; }
                  38%, 55%  { opacity: 0.55; }
                  68%, 100% { opacity: 0; }
                }
              `}</style>

              {/* The pink glow that follows the drag */}
              <div style={{
                position: 'absolute', inset: 0, borderRadius: 24,
                background: `radial-gradient(circle at 75% 50%, ${PINK}88, transparent 65%)`,
                animation: 'likeGlow 4.4s ease-in-out infinite',
                pointerEvents: 'none', filter: 'blur(8px)',
              }} />

              {/* The opportunity card */}
              <div style={{
                position: 'absolute', inset: 0, borderRadius: 24,
                background: `linear-gradient(180deg, ${PURPLE} 0%, ${PINK} 55%, ${BLUE} 100%)`,
                animation: 'cardSwipeLoop 4.4s cubic-bezier(0.4, 0, 0.2, 1) infinite',
                boxShadow: `0 18px 50px rgba(0,0,0,0.55), 0 0 0 1px ${PANEL_BORDER} inset`,
                overflow: 'hidden',
              }}>
                {/* Card content */}
                <div style={{
                  position: 'absolute', top: 18, left: 18, right: 18,
                  display: 'flex', gap: 6, flexWrap: 'wrap',
                }}>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
                    padding: '4px 8px', borderRadius: 999,
                    background: 'rgba(0,0,0,0.45)', color: TEXT,
                    backdropFilter: 'blur(6px)',
                  }}>URGENT</span>
                  <span style={{
                    fontSize: 9, fontWeight: 800, letterSpacing: '0.12em',
                    padding: '4px 8px', borderRadius: 999,
                    background: 'rgba(0,0,0,0.45)', color: TEXT,
                    backdropFilter: 'blur(6px)',
                  }}>BEGINNER FRIENDLY</span>
                </div>
                <div style={{
                  position: 'absolute', left: 20, right: 20, bottom: 20,
                  color: TEXT, fontFamily: FONT_STACK,
                }}>
                  <p style={{ margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', opacity: 0.85 }}>
                    QUIET LENS STUDIO
                  </p>
                  <p style={{ margin: '4px 0 8px', fontSize: 22, fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                    Photobooth Assistant
                  </p>
                  <p style={{ margin: 0, fontSize: 12, opacity: 0.9 }}>
                    Sat 14 Jun · <strong>£14/hr</strong> · London
                  </p>
                </div>
              </div>

              {/* The animated finger indicator */}
              <div style={{
                position: 'absolute', bottom: 80, left: '50%', marginLeft: -16,
                width: 32, height: 32, borderRadius: '50%',
                background: 'rgba(255,255,255,0.92)',
                boxShadow: `0 6px 18px rgba(0,0,0,0.45), 0 0 0 1px ${PANEL_BORDER} inset`,
                animation: 'fingerLoop 4.4s cubic-bezier(0.4, 0, 0.2, 1) infinite',
                pointerEvents: 'none',
              }} />

              {/* The "Interested ❤️" badge */}
              <div style={{
                position: 'absolute', top: '42%', right: -8,
                padding: '8px 14px', borderRadius: 999,
                background: `linear-gradient(135deg, ${PINK}, ${PURPLE})`,
                color: TEXT, fontFamily: FONT_STACK, fontWeight: 900, fontSize: 13,
                boxShadow: `0 8px 24px ${PINK}66`,
                animation: 'likeBadgePulse 4.4s cubic-bezier(0.4, 0, 0.2, 1) infinite',
                pointerEvents: 'none',
                display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>
                <span style={{ display: 'inline-block', animation: 'heartBeat 0.5s ease-in-out 1.7s infinite' }}>❤️</span>
                Interested
              </div>

              {/* Caption beneath */}
              <p style={{
                position: 'absolute', bottom: -34, left: 0, right: 0,
                textAlign: 'center', fontFamily: FONT_STACK,
                fontSize: 12, fontWeight: 700, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: TEXT_MUTED,
              }}>
                ← swipe right to apply →
              </p>
            </div>
          </div>
        </section>

        {/* ── CREW SPOTLIGHT — live momentum row ── */}
        <CrewSpotlightRow />

        {/* ── SECTION 1 — HOW IT WORKS ── */}
        <section style={{ padding: 'clamp(40px, 6vw, 80px) 0' }}>
          <SectionEyebrow>✦ How it works</SectionEyebrow>
          <h2 style={{
            margin: '14px 0 36px', fontFamily: FONT_STACK,
            fontWeight: 900, fontSize: 'clamp(28px, 4vw, 44px)',
            letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            From swipe to shift in four taps.
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            <StepCard num="1" title="Create your profile" icon={Users} />
            <StepCard num="2" title="Swipe opportunities" icon={Lightning} />
            <StepCard num="3" title="Get involved" icon={HandHeart} />
            <StepCard num="4" title="Build confidence & references" icon={SealCheck} />
          </div>
        </section>

        {/* ── SECTION 2 — TEAM ── */}
        <section style={{ padding: 'clamp(40px, 6vw, 80px) 0' }}>
          <div style={{
            padding: 'clamp(28px, 5vw, 56px)',
            background: `linear-gradient(135deg, ${PURPLE}1A 0%, ${PINK}10 100%)`,
            border: `1px solid ${PANEL_BORDER}`, borderRadius: 28,
            backdropFilter: 'blur(12px)',
          }}>
            <SectionEyebrow>✦ EventDrop Team</SectionEyebrow>
            <h2 style={{
              margin: '12px 0 16px', fontFamily: FONT_STACK,
              fontWeight: 900, fontSize: 'clamp(26px, 4vw, 42px)',
              letterSpacing: '-0.02em', lineHeight: 1.12,
            }}>
              Find reliable crew in 30 seconds.
            </h2>
            <p style={{ maxWidth: 540, color: TEXT_MUTED, fontSize: 16, lineHeight: 1.6, marginBottom: 20 }}>
              For suppliers, venues and event businesses who need extra hands fast —
              without dragging anyone through corporate recruitment software.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', maxWidth: 520 }}>
              <FeatureRow label="Quick opportunity posting" />
              <FeatureRow label="Trial shifts" />
              <FeatureRow label="Ongoing roles" />
              <FeatureRow label="Last-minute urgent requests" />
              <FeatureRow label="Beginner-friendly toggles" />
            </ul>
            <HeroCta label="Post Opportunity" primary onClick={() => navigate('/dashboard/supplier/crew/new')} testId="section-team-post" />
          </div>
        </section>

        {/* ── SECTION 3 — CREW ── */}
        <section style={{ padding: 'clamp(40px, 6vw, 80px) 0' }}>
          <div style={{
            padding: 'clamp(28px, 5vw, 56px)',
            background: `linear-gradient(135deg, ${BLUE}14 0%, ${PURPLE}14 100%)`,
            border: `1px solid ${PANEL_BORDER}`, borderRadius: 28,
            backdropFilter: 'blur(12px)',
          }}>
            <SectionEyebrow>✦ EventDrop Crew</SectionEyebrow>
            <h2 style={{
              margin: '12px 0 16px', fontFamily: FONT_STACK,
              fontWeight: 900, fontSize: 'clamp(26px, 4vw, 42px)',
              letterSpacing: '-0.02em', lineHeight: 1.12,
            }}>
              Build your future one event at a time.
            </h2>
            <p style={{ maxWidth: 540, color: TEXT_MUTED, fontSize: 16, lineHeight: 1.6, marginBottom: 20 }}>
              For aspiring young people who want real event experience, confidence,
              and references — without uploading a CV.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', maxWidth: 520 }}>
              <FeatureRow label="Swipe opportunities" />
              <FeatureRow label="Build confidence" />
              <FeatureRow label="Gain references" />
              <FeatureRow label="Earn badges" />
              <FeatureRow label="Bring a friend" />
              <FeatureRow label="Real event experience" />
            </ul>
            <HeroCta label="Join Crew" primary onClick={() => navigate('/crew/feed')} testId="section-crew-join" />
          </div>
        </section>

        {/* ── SECTION 4 — LIVE OPPORTUNITIES ── */}
        <section style={{ padding: 'clamp(40px, 6vw, 80px) 0' }}>
          <SectionEyebrow>✦ Live opportunities</SectionEyebrow>
          <h2 style={{
            margin: '14px 0 28px', fontFamily: FONT_STACK,
            fontWeight: 900, fontSize: 'clamp(28px, 4vw, 44px)',
            letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            What's open right now.
          </h2>
          <div style={{
            display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 14,
            scrollbarWidth: 'thin',
          }}
            data-testid="live-opportunities-row"
          >
            {previewCards.map((o, i) => (
              <OpportunityPreviewCard key={i} {...o} />
            ))}
          </div>
          <div style={{ marginTop: 22 }}>
            <HeroCta label="Open the feed" onClick={() => navigate('/crew/feed')} testId="section-live-feed" />
          </div>
        </section>

        {/* ── SECTION 5 — BADGES ── */}
        <section style={{ padding: 'clamp(40px, 6vw, 80px) 0 100px' }}>
          <SectionEyebrow>✦ Badges & progression</SectionEyebrow>
          <h2 style={{
            margin: '14px 0 18px', fontFamily: FONT_STACK,
            fontWeight: 900, fontSize: 'clamp(28px, 4vw, 44px)',
            letterSpacing: '-0.02em', lineHeight: 1.1,
          }}>
            Level up your experience.
          </h2>
          <p style={{ maxWidth: 540, color: TEXT_MUTED, fontSize: 16, lineHeight: 1.6, marginBottom: 28 }}>
            Every shift earns a mark of trust. Show up, do the work, collect the badges.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <BadgeChip label="First Shift" color={PINK} />
            <BadgeChip label="Team Player" color={BLUE} />
            <BadgeChip label="Reliable Crew" color={PURPLE} />
            <BadgeChip label="Event Pro" color={PINK} />
            <BadgeChip label="Guest Favourite" color={BLUE} />
          </div>
          {/* Decorative icon row */}
          <div style={{ display: 'flex', gap: 18, marginTop: 36, opacity: 0.5 }}>
            <Camera size={26} color={TEXT_MUTED} weight="fill" />
            <MusicNote size={26} color={TEXT_MUTED} weight="fill" />
            <Confetti size={26} color={TEXT_MUTED} weight="fill" />
            <Buildings size={26} color={TEXT_MUTED} weight="fill" />
          </div>
        </section>

        {/* ── FOOTER ── */}
        <footer style={{
          padding: '32px 0', borderTop: `1px solid ${PANEL_BORDER}`,
          fontFamily: FONT_STACK, fontSize: 12, color: TEXT_MUTED,
          display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
        }}>
          <span>EventDrop Crew · part of EventDrop Team</span>
          <a href="/" style={{ color: TEXT_MUTED, textDecoration: 'none' }}>← Back to EventDrop</a>
        </footer>
      </div>
    </div>
  );
};

export default CrewLandingPage;
